import { blockCatalog, type BlockInput } from './blocks.ts';
import type { LandingContent } from './landing.ts';
import type { templateKeys } from './pages.ts';

export type TemplateKey = (typeof templateKeys)[number];

export interface LandingTemplate {
  key: TemplateKey;
  label: string;
  description: string;
  /** Section types in order. Every template has exactly one hero and one form so it can be published once placeholders are replaced. */
  sections: (keyof typeof blockCatalog)[];
}

/** Starting structures (step 1 §15 step 4). They provide structure and clearly marked placeholder copy, never invented claims. */
export const landingTemplates: LandingTemplate[] = [
  { key: 'enterprise-service', label: 'Enterprise service', description: 'Problem, capabilities, architecture, process, FAQ. For complex buying decisions.', sections: ['hero', 'trustBar', 'problem', 'services', 'architecture', 'process', 'timeline', 'techStack', 'caseStudies', 'faq', 'cta', 'form'] },
  { key: 'startup-mvp', label: 'Startup MVP', description: 'Idea to launch arc with engagement options.', sections: ['hero', 'problem', 'benefits', 'process', 'timeline', 'pricing', 'faq', 'form', 'stickyCta'] },
  { key: 'crm', label: 'CRM', description: 'Pipelines, permissions and integrations for sales-process buyers.', sections: ['hero', 'benefits', 'features', 'integrations', 'process', 'faq', 'form'] },
  { key: 'mobile-app', label: 'Mobile app', description: 'Platforms, offline and hardware, store publishing.', sections: ['hero', 'services', 'architecture', 'techStack', 'process', 'faq', 'form', 'stickyCta'] },
  { key: 'ecommerce', label: 'E-commerce', description: 'Catalog, checkout, payments and ERP integration.', sections: ['hero', 'services', 'integrations', 'process', 'faq', 'cta', 'form'] },
  { key: 'industry', label: 'Industry-specific', description: 'Sector problems, use cases and integrations.', sections: ['hero', 'problem', 'useCases', 'integrations', 'security', 'process', 'faq', 'form'] },
  { key: 'lead-gen-minimal', label: 'Lead-gen minimal', description: 'Short ad landing: promise, three benefits, form.', sections: ['hero', 'benefits', 'faq', 'form'] },
  { key: 'long-form-seo', label: 'Long-form SEO', description: 'Deep explanatory page for organic search.', sections: ['hero', 'problem', 'features', 'useCases', 'process', 'faq', 'relatedServices', 'cta', 'form'] },
  { key: 'comparison', label: 'Comparison', description: 'Options side by side for evaluating buyers.', sections: ['hero', 'comparison', 'benefits', 'faq', 'cta', 'form'] },
];

export function buildTemplate(key: TemplateKey, ctx: { name: string; robots?: 'index' | 'noindex' }): LandingContent {
  const tpl = landingTemplates.find((t) => t.key === key) ?? landingTemplates[6]!;
  const blocks = tpl.sections.map((type) => {
    const b = blockCatalog[type].create(type) as BlockInput & Record<string, unknown>;
    if (b.type === 'hero') b.headline = `[Edit] ${ctx.name}: a specific headline`;
    return b;
  });
  return {
    seo: { title: ctx.name.slice(0, 70), description: `[Edit] Describe ${ctx.name} in one or two sentences for search results.`.slice(0, 170), robots: ctx.robots ?? 'index' },
    blocks,
  } as unknown as LandingContent;
}
